import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  type Dispatch,
} from 'react';
import { useLocation } from 'wouter';

import type { ConversationMode } from '@shared/conversation-types';

import {
  createInitialState,
  reduce,
  type LifecycleEvent,
} from './conversationLifecycle';

const STORAGE_KEY_PREFIX = 'v2.conversation.lifecycle.';
const STORAGE_TTL_MS = 60 * 60 * 1000;

const CONVERSATION_MODES: readonly ConversationMode[] = [
  'home',
  'scoping',
  'update',
  'interview',
];

function isConversationMode(x: unknown): x is ConversationMode {
  return typeof x === 'string' && CONVERSATION_MODES.includes(x as ConversationMode);
}

type StoredLifecycleV1 = {
  conversationId: string;
  mode: ConversationMode;
  shortId: string | null;
  /** Spec 012 — the project the thread is bound to (S8 buffer round-trip). */
  boundProjectId: string | null;
  lastActivityAt: number;
  savedAt: number;
};

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Remove every persisted conversation-lifecycle session (any user) from
 * sessionStorage. Used on a hard tenancy reset — notably an organisation switch
 * — so a stale conversation id from the previous org cannot be recovered on the
 * next mount. Safe in private mode / SSR (swallows storage errors).
 */
export function clearStoredConversationLifecycles(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k != null && k.startsWith(STORAGE_KEY_PREFIX)) stale.push(k);
    }
    for (const k of stale) sessionStorage.removeItem(k);
  } catch {
    // no sessionStorage (SSR) / quota / private mode — nothing to clear
  }
}

/** `/projects/:id/...` → short id; otherwise `null` (home / unknown). */
function deriveShortIdFromPathname(pathname: string): string | null {
  const m = /^\/projects\/([^/]+)/.exec(pathname);
  return m ? m[1] : null;
}

function tryParseStoredLifecycle(
  raw: string,
  now: number,
): Omit<StoredLifecycleV1, 'savedAt'> | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed == null || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;

  if (typeof o.savedAt !== 'number' || now - o.savedAt > STORAGE_TTL_MS) {
    return null;
  }
  if (typeof o.conversationId !== 'string' || !o.conversationId) return null;
  if (!isConversationMode(o.mode)) return null;

  const shortId =
    o.shortId === null || typeof o.shortId === 'string' ? o.shortId : null;
  // Older buffers (pre-012) won't have boundProjectId — default to unbound.
  const boundProjectId =
    o.boundProjectId === null || typeof o.boundProjectId === 'string'
      ? o.boundProjectId
      : null;

  if (typeof o.lastActivityAt !== 'number' || !Number.isFinite(o.lastActivityAt)) {
    return null;
  }

  return {
    conversationId: o.conversationId,
    mode: o.mode,
    shortId,
    boundProjectId,
    lastActivityAt: o.lastActivityAt,
  };
}

export type UseConversationLifecycleArgs = {
  userId: string;
  /**
   * The caller's active organisation id. When it changes between two resolved
   * orgs (a deliberate org switch), the conversation is torn down so no id /
   * transcript from the previous org survives into the new tenant. `null` /
   * `undefined` while the org is still resolving — those transitions never
   * trigger a teardown (see the org-change effect).
   */
  orgId?: string | null;
};

export type UseConversationLifecycleReturn = {
  conversationId: string | null;
  mode: ConversationMode;
  isReady: boolean;
  isEnded: boolean;
  /**
   * Why the conversation ended: `guardrail` (011 F3), an intentional agent end
   * `complete`/`scope_locked`/`cap` (ANA-575), or `null` (idle TTL / not ended).
   */
  endReason: 'guardrail' | 'complete' | 'scope_locked' | 'cap' | null;
  lastActivityAt: number;
  /** Spec 012 — the project the thread is currently bound to (`null` = unbound/lobby). */
  boundProjectId: string | null;
  /** ANA-309 — the id recovered from sessionStorage this mount (`null` once re-minted). */
  recoveredId: string | null;
  sendMessage: () => void;
  reset: () => void;
  /**
   * Re-open an idle/normally-ended conversation in place (keeps the id +
   * transcript). Used by the returning entry card's "pick up where you left
   * off" pick; no-op on a guardrail termination.
   */
  resumeFromEnded: () => void;
  dispatch: Dispatch<LifecycleEvent>;
};

export function useConversationLifecycle(
  args: UseConversationLifecycleArgs,
): UseConversationLifecycleReturn {
  const [pathname] = useLocation();

  const [state, dispatch] = useReducer(
    reduce,
    undefined,
    () => createInitialState(Date.now()),
  );

  useLayoutEffect(() => {
    // Pre-auth: never share a single `anonymous` namespace across visitors or
    // pre→post-auth transitions in the same browser profile.
    if (args.userId === 'anonymous') return;

    const raw = sessionStorage.getItem(storageKey(args.userId));
    if (raw == null) return;

    const recovered = tryParseStoredLifecycle(raw, Date.now());
    if (recovered == null) return;

    dispatch({
      kind: 'recover_from_storage',
      conversationId: recovered.conversationId,
      mode: recovered.mode,
      shortId: recovered.shortId,
      boundProjectId: recovered.boundProjectId,
      at: recovered.lastActivityAt,
    });
  }, [args.userId]);

  useEffect(() => {
    dispatch({
      kind: 'pathname_changed',
      pathname,
      shortId: deriveShortIdFromPathname(pathname),
    });
  }, [pathname]);

  useEffect(() => {
    if (args.userId === 'anonymous') return;
    if (state.conversationId === null) return;

    try {
      const payload: StoredLifecycleV1 = {
        conversationId: state.conversationId,
        mode: state.mode,
        shortId: state.activeShortId,
        boundProjectId: state.boundProjectId,
        lastActivityAt: state.lastActivityAt,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(
        storageKey(args.userId),
        JSON.stringify(payload),
      );
    } catch {
      // Quota / private mode — ignore
    }
  }, [
    args.userId,
    state.conversationId,
    state.mode,
    state.activeShortId,
    state.boundProjectId,
    state.lastActivityAt,
  ]);

  // Organisation switch → hard tenancy reset. A client-owned conversation id is
  // scoped to one org; carrying it into another org is a cross-tenant data leak
  // (and the server now rejects it). When the active org changes between two
  // *resolved* values, drop the persisted session and clear the in-memory
  // conversation so the new org starts clean. We deliberately skip the initial
  // mount and the `null → org` resolve-on-load (prev == null) so a freshly
  // recovered session is never wiped just because the org id arrived a tick late.
  const prevOrgIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevOrgIdRef.current;
    prevOrgIdRef.current = args.orgId;
    if (prev === undefined || prev === null) return; // initial mount / first resolve
    if (args.orgId == null || args.orgId === prev) return; // no real switch
    if (args.userId !== 'anonymous') {
      try {
        sessionStorage.removeItem(storageKey(args.userId));
      } catch {
        // private mode / quota — ignore
      }
    }
    dispatch({ kind: 'cleared', at: Date.now() });
  }, [args.orgId, args.userId]);

  const sendMessage = useCallback(() => {
    dispatch({ kind: 'user_message_sent', at: Date.now() });
  }, []);

  const reset = useCallback(() => {
    dispatch({ kind: 'explicit_reset', at: Date.now() });
  }, []);

  const resumeFromEnded = useCallback(() => {
    dispatch({ kind: 'resume_from_ended', at: Date.now() });
  }, []);

  return useMemo(
    () => ({
      conversationId: state.conversationId,
      mode: state.mode,
      isReady: state.isReady,
      isEnded: state.isEnded,
      endReason: state.endReason,
      lastActivityAt: state.lastActivityAt,
      boundProjectId: state.boundProjectId,
      recoveredId: state.recoveredId,
      sendMessage,
      reset,
      resumeFromEnded,
      dispatch,
    }),
    [
      state.conversationId,
      state.mode,
      state.isReady,
      state.isEnded,
      state.endReason,
      state.lastActivityAt,
      state.boundProjectId,
      state.recoveredId,
      sendMessage,
      reset,
      resumeFromEnded,
      dispatch,
    ],
  );
}
